/**
 * IECN design system.
 *
 * Every interactive element carries hover, focus-visible and active states. Only transform
 * and opacity are animated. Status is never communicated by colour alone — there is always
 * a label, and usually an icon or dot as well.
 */
import type { ButtonHTMLAttributes, InputHTMLAttributes, ReactNode, SelectHTMLAttributes } from 'react';
import { forwardRef } from 'react';
import { Link } from 'react-router-dom';
import { formatMoney, statusTone, type DeliveryStatus, type StatusTone } from '@iecn/shared';

function cx(...values: Array<string | false | null | undefined>): string {
  return values.filter(Boolean).join(' ');
}

// --- Button ------------------------------------------------------------------

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
type ButtonSize = 'sm' | 'md' | 'lg' | 'field';

const BUTTON_BASE =
  'inline-flex items-center justify-center gap-2 rounded-xl font-semibold tracking-tight ' +
  'transition-[transform,opacity,box-shadow,background-color] duration-200 ease-spring ' +
  'disabled:opacity-45 disabled:cursor-not-allowed disabled:pointer-events-none select-none';

const BUTTON_VARIANTS: Record<ButtonVariant, string> = {
  primary:
    'bg-gold-500 text-navy-950 shadow-gold-glow hover:bg-gold-400 hover:-translate-y-0.5 active:translate-y-0 active:bg-gold-600',
  secondary:
    'bg-navy-900 text-white shadow-raised hover:bg-navy-800 hover:-translate-y-0.5 active:translate-y-0',
  ghost:
    'bg-white/70 text-navy-900 ring-1 ring-navy-100 hover:bg-white hover:ring-navy-200 active:bg-navy-50',
  danger: 'bg-red-700 text-white shadow-raised hover:bg-red-600 active:bg-red-800',
};

const BUTTON_SIZES: Record<ButtonSize, string> = {
  sm: 'text-sm px-3 py-1.5',
  md: 'text-[15px] px-5 py-2.5',
  lg: 'text-base px-7 py-3.5',
  /** Field mode: large enough for a gloved thumb in a loading dock. */
  field: 'text-lg px-6 py-5 w-full min-h-[68px]',
};

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'primary', size = 'md', loading, className, children, disabled, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      className={cx(BUTTON_BASE, BUTTON_VARIANTS[variant], BUTTON_SIZES[size], className)}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...rest}
    >
      {loading && <Spinner className="h-4 w-4" />}
      {children}
    </button>
  );
});

export function ButtonLink({
  to,
  variant = 'primary',
  size = 'md',
  className,
  children,
}: {
  to: string;
  variant?: ButtonVariant;
  size?: ButtonSize;
  className?: string;
  children: ReactNode;
}) {
  return (
    <Link
      to={to}
      className={cx(BUTTON_BASE, BUTTON_VARIANTS[variant], BUTTON_SIZES[size], className)}
    >
      {children}
    </Link>
  );
}

// --- Surfaces ----------------------------------------------------------------

/** base → elevated → floating. Surfaces do not all sit on the same plane. */
export function Card({
  children,
  className,
  elevation = 'raised',
  as: Tag = 'div',
}: {
  children: ReactNode;
  className?: string;
  elevation?: 'flat' | 'raised' | 'elevated' | 'floating';
  as?: 'div' | 'section' | 'article' | 'li';
}) {
  const shadows = {
    flat: 'shadow-none ring-1 ring-navy-100',
    raised: 'shadow-raised',
    elevated: 'shadow-elevated',
    floating: 'shadow-floating',
  };
  return (
    <Tag className={cx('rounded-2xl bg-white', shadows[elevation], className)}>{children}</Tag>
  );
}

export function Section({
  title,
  description,
  action,
  children,
  className,
}: {
  title: string;
  description?: string;
  action?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section className={cx('space-y-4', className)}>
      <div className="flex items-end justify-between gap-4">
        <div>
          <h2 className="text-xl text-navy-950">{title}</h2>
          {description && <p className="mt-1 text-sm text-slate-500">{description}</p>}
        </div>
        {action}
      </div>
      {children}
    </section>
  );
}

// --- Status ------------------------------------------------------------------

const TONE_STYLES: Record<StatusTone, { chip: string; dot: string }> = {
  neutral: { chip: 'bg-slate-100 text-slate-700 ring-slate-200', dot: 'bg-slate-500' },
  info: { chip: 'bg-navy-50 text-navy-800 ring-navy-100', dot: 'bg-navy-600' },
  active: { chip: 'bg-cyan-50 text-cyan-900 ring-cyan-100', dot: 'bg-cyan-600' },
  success: { chip: 'bg-emerald-50 text-emerald-900 ring-emerald-100', dot: 'bg-emerald-600' },
  warning: { chip: 'bg-amber-50 text-amber-900 ring-amber-200', dot: 'bg-amber-600' },
  critical: { chip: 'bg-red-50 text-red-900 ring-red-200', dot: 'bg-red-600' },
};

export function StatusChip({
  label,
  tone,
  status,
  pulse,
  className,
}: {
  label: string;
  tone?: StatusTone;
  status?: DeliveryStatus;
  pulse?: boolean;
  className?: string;
}) {
  const resolved = tone ?? (status ? statusTone(status) : 'neutral');
  const style = TONE_STYLES[resolved];
  return (
    <span
      className={cx(
        'inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ring-1',
        style.chip,
        className,
      )}
    >
      <span className="relative flex h-1.5 w-1.5">
        {pulse && (
          <span
            className={cx('absolute inline-flex h-full w-full rounded-full animate-pulse-ring', style.dot)}
          />
        )}
        <span className={cx('relative inline-flex h-1.5 w-1.5 rounded-full', style.dot)} />
      </span>
      {label}
    </span>
  );
}

// --- Money and metrics -------------------------------------------------------

export function Money({
  cents,
  currency = 'USD',
  className,
}: {
  cents: number;
  currency?: string;
  className?: string;
}) {
  return <span className={cx('tabular-nums', className)}>{formatMoney(cents, currency)}</span>;
}

export function Metric({
  label,
  value,
  sub,
  tone = 'neutral',
}: {
  label: string;
  value: ReactNode;
  sub?: ReactNode;
  tone?: StatusTone;
}) {
  return (
    <Card className="p-5" elevation="raised">
      <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{label}</div>
      <div className="mt-2 text-3xl font-semibold tracking-tighter text-navy-950 tabular-nums">
        {value}
      </div>
      {sub && (
        <div className={cx('mt-1 text-sm', tone === 'critical' ? 'text-red-700' : 'text-slate-500')}>
          {sub}
        </div>
      )}
    </Card>
  );
}

// --- Form --------------------------------------------------------------------

export function Field({
  label,
  hint,
  error,
  required,
  children,
  htmlFor,
}: {
  label: string;
  hint?: string;
  error?: string;
  required?: boolean;
  children: ReactNode;
  htmlFor?: string;
}) {
  return (
    <div className="space-y-1.5">
      <label htmlFor={htmlFor} className="block text-sm font-semibold text-navy-900">
        {label}
        {required && <span className="ml-1 text-red-700" aria-hidden="true">*</span>}
        {required && <span className="sr-only"> (required)</span>}
      </label>
      {children}
      {error ? (
        <p className="text-sm font-medium text-red-700" role="alert">
          {error}
        </p>
      ) : (
        hint && <p className="text-sm text-slate-500">{hint}</p>
      )}
    </div>
  );
}

const CONTROL =
  'w-full rounded-xl border-0 bg-white px-4 py-3 text-[15px] text-ink ring-1 ring-navy-100 ' +
  'placeholder:text-slate-400 transition-shadow duration-200 ' +
  'hover:ring-navy-200 focus:ring-2 focus:ring-navy-600 disabled:bg-slate-50 disabled:text-slate-400';

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement> & { invalid?: boolean }>(
  function Input({ className, invalid, ...rest }, ref) {
    return (
      <input
        ref={ref}
        aria-invalid={invalid || undefined}
        className={cx(CONTROL, invalid && 'ring-2 ring-red-600', className)}
        {...rest}
      />
    );
  },
);

export const Select = forwardRef<HTMLSelectElement, SelectHTMLAttributes<HTMLSelectElement>>(
  function Select({ className, children, ...rest }, ref) {
    return (
      <select ref={ref} className={cx(CONTROL, 'appearance-none pr-10', className)} {...rest}>
        {children}
      </select>
    );
  },
);

export function Textarea({
  className,
  ...rest
}: React.TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <textarea className={cx(CONTROL, 'min-h-[96px] resize-y', className)} {...rest} />;
}

/** Large tappable option card — used for service level and vehicle choice. */
export function OptionCard({
  selected,
  onSelect,
  title,
  subtitle,
  meta,
  disabled,
}: {
  selected: boolean;
  onSelect: () => void;
  title: string;
  subtitle?: string;
  meta?: ReactNode;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      disabled={disabled}
      aria-pressed={selected}
      className={cx(
        'group w-full rounded-2xl p-4 text-left transition-[transform,box-shadow,background-color] duration-200 ease-spring',
        'disabled:opacity-40 disabled:cursor-not-allowed',
        selected
          ? 'bg-navy-900 text-white shadow-elevated'
          : 'bg-white ring-1 ring-navy-100 hover:ring-navy-300 hover:-translate-y-0.5 shadow-raised',
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className={cx('font-semibold tracking-tight', selected ? 'text-white' : 'text-navy-950')}>
            {title}
          </div>
          {subtitle && (
            <div className={cx('mt-0.5 text-sm', selected ? 'text-navy-100' : 'text-slate-500')}>
              {subtitle}
            </div>
          )}
        </div>
        {meta && (
          <div className={cx('shrink-0 font-semibold tabular-nums', selected ? 'text-gold-400' : 'text-navy-900')}>
            {meta}
          </div>
        )}
      </div>
    </button>
  );
}

// --- Feedback ----------------------------------------------------------------

export function Spinner({ className }: { className?: string }) {
  return (
    <svg className={cx('animate-spin', className)} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeOpacity="0.25" strokeWidth="3" />
      <path d="M22 12a10 10 0 0 0-10-10" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
    </svg>
  );
}

export function Alert({
  tone = 'info',
  title,
  children,
  className,
}: {
  tone?: StatusTone;
  title?: string;
  children: ReactNode;
  className?: string;
}) {
  const style = TONE_STYLES[tone];
  return (
    <div className={cx('rounded-xl px-4 py-3 text-sm ring-1', style.chip, className)} role="status">
      {title && <div className="font-semibold">{title}</div>}
      <div className={title ? 'mt-0.5' : undefined}>{children}</div>
    </div>
  );
}

export function EmptyState({
  title,
  description,
  action,
}: {
  title: string;
  description: string;
  action?: ReactNode;
}) {
  return (
    <div className="rounded-2xl border border-dashed border-navy-200 bg-white/60 px-6 py-12 text-center">
      <h3 className="text-lg text-navy-900">{title}</h3>
      <p className="mx-auto mt-2 max-w-md text-sm text-slate-500">{description}</p>
      {action && <div className="mt-5 flex justify-center">{action}</div>}
    </div>
  );
}

export function Skeleton({ className }: { className?: string }) {
  return <div className={cx('iecn-skeleton rounded-xl', className)} />;
}

export function ErrorPanel({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  const message =
    error instanceof Error ? error.message : 'Something went wrong. Please try again.';
  return (
    <Alert tone="critical" title="That did not work">
      <p>{message}</p>
      {onRetry && (
        <Button variant="ghost" size="sm" className="mt-3" onClick={onRetry}>
          Try again
        </Button>
      )}
    </Alert>
  );
}

export { cx };
